import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Headphones, Send, Loader2, Wifi, CheckCircle, MessageSquarePlus, XCircle } from 'lucide-react';
import { useSarLabAuth } from '../contexts/SarLabAuthContext';
import { useLiveSupport } from '../contexts/LiveSupportContext';
import { useWidgetConfig } from '../contexts/WidgetConfigContext';

// Helper function to adjust color brightness
const adjustColor = (color: string, amount: number): string => {
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

interface LiveSupportChatProps {
  onClose: () => void;
  onStartNewChat?: () => void;
}

const LiveSupportChat: React.FC<LiveSupportChatProps> = ({ onClose, onStartNewChat }) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useSarLabAuth();
  const { config } = useWidgetConfig();
  const {
    activeTicket,
    messages,
    isConnected,
    isLoadingTicket,
    typingUser,
    createTicket,
    sendSupportMessage,
    sendTyping,
    stopTyping,
    closeTicket,
  } = useLiveSupport();

  const isTicketClosed = activeTicket?.status === 'closed';

  // Get colors from config
  const primaryColor = config.window?.primaryColor || '#6366f1';
  const lighterColor = adjustColor(primaryColor, 40);
  const greetingMessage = config.window?.greetingMessage;

  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);

    if (!isTyping && e.target.value.trim()) {
      setIsTyping(true);
      sendTyping();
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      stopTyping();
    }, 2000);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !isLoggedIn) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    setIsTyping(false);
    stopTyping();

    setSending(true);
    try {
      if (!activeTicket) {
        await createTicket('other', messageContent);
      } else {
        await sendSupportMessage(messageContent);
      }
      // Focus back to input
      setTimeout(() => inputRef.current?.focus(), 0);
    } catch (error) {
      console.error('Failed to send message:', error);
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleStartNewChat = async () => {
    // Close the current ticket and clear state so user can start fresh
    await closeTicket();
    // Notify parent to switch to support form view
    if (onStartNewChat) {
      onStartNewChat();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b border-dark-700/50">
        <div className="flex items-center space-x-3">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: primaryColor }}
          >
            <Headphones className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="text-sm font-medium text-white">{t('support:chat.supportTitle')}</span>
            <div className="flex items-center space-x-1 text-xs text-gray-400">
              {isConnected ? (
                <>
                  <Wifi className="w-3 h-3" style={{ color: primaryColor }} />
                  <span style={{ color: lighterColor }}>{t('support:chat.connected')}</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }}></span>
                  <span>{t('support:chat.online')}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="p-1 hover:bg-yellow-500/20 rounded transition-colors duration-200"
            title={t('support:chat.minimize')}
          >
            <svg className="w-4 h-4 text-gray-400 hover:text-yellow-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="6" y1="12" x2="18" y2="12" />
            </svg>
          </button>
          {activeTicket && activeTicket.status !== 'closed' && !showEndConfirm && (
            <button
              onClick={() => setShowEndConfirm(true)}
              className="p-1 hover:bg-red-500/20 rounded transition-colors duration-200"
              title={t('support:chat.endConversation')}
            >
              <XCircle className="w-4 h-4 text-gray-400 hover:text-red-400" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {!isLoggedIn ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Headphones className="w-12 h-12 text-gray-500 mb-4" />
            <p className="text-gray-400 text-sm">
              {t('support:chat.loginRequired')}
            </p>
          </div>
        ) : isLoadingTicket ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: primaryColor }}
            >
              <Headphones className="w-8 h-8 text-white" />
            </motion.div>
            <h3 className="text-lg font-bold text-white mb-2">{t('support:chat.greeting')}</h3>
            <p className="text-gray-400 text-sm">
              {greetingMessage || t('support:chat.greetingDefault')}
            </p>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex flex-col max-w-[80%] ${msg.senderRole === 'user' ? 'self-start' : 'self-end'}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="text-xs font-semibold"
                    style={{ color: msg.senderRole === 'user' ? lighterColor : '#818cf8' }}
                  >
                    {msg.senderName}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(msg.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div
                  className={`rounded-lg px-4 py-2 text-gray-100 ${
                    msg.senderRole === 'user' ? 'rounded-tl-sm' : 'rounded-tr-sm'
                  }`}
                  style={{
                    backgroundColor: msg.senderRole === 'user' ? `${primaryColor}15` : 'rgba(99, 102, 241, 0.1)',
                    border: msg.senderRole === 'user' ? `1px solid ${primaryColor}30` : '1px solid rgba(99, 102, 241, 0.2)',
                  }}
                >
                  <p className="text-sm">{msg.text}</p>
                </div>
              </motion.div>
            ))}
            {typingUser && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="bg-dark-700 rounded-lg px-4 py-2">
                  <div className="flex items-center space-x-1">
                    <span className="text-gray-400 text-sm">{typingUser} {t('support:chat.writingMessage')}</span>
                    <div className="flex space-x-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Inline End Chat Confirmation */}
      {showEndConfirm && (
        <div className="p-4 border-t border-dark-700/50 bg-red-500/10">
          <div className="flex flex-col items-center space-y-3">
            <p className="text-sm text-gray-300 text-center">
              {t('support:chat.endConfirm')}
            </p>
            <div className="flex items-center space-x-3">
              <button
                onClick={async () => {
                  await closeTicket();
                  setShowEndConfirm(false);
                  onClose();
                }}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition-colors"
              >
                {t('support:chat.endYes')}
              </button>
              <button
                onClick={() => setShowEndConfirm(false)}
                className="px-4 py-2 bg-dark-600 hover:bg-dark-500 text-gray-300 text-sm rounded-lg transition-colors"
              >
                {t('common:buttons.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoggedIn && !showEndConfirm && (
        <div className="p-4 border-t border-dark-700/50">
          {isTicketClosed ? (
            <div className="flex flex-col items-center space-y-3">
              <div className="flex items-center space-x-2 text-gray-400">
                <CheckCircle className="w-5 h-5" style={{ color: primaryColor }} />
                <span className="text-sm">{t('support:chat.conversationClosed')}</span>
              </div>
              <button
                onClick={handleStartNewChat}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                <MessageSquarePlus className="w-4 h-4 text-white" />
                <span className="text-white text-sm">{t('support:chat.startNewChat')}</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <input
                ref={inputRef}
                type="text"
                value={newMessage}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder={t('support:prechat.messagePlaceholder')}
                className="flex-1 bg-dark-700/50 border border-dark-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none text-sm"
                style={{ ['--focus-color' as string]: primaryColor }}
                onFocus={(e) => e.target.style.borderColor = primaryColor}
                onBlur={(e) => e.target.style.borderColor = ''}
                disabled={sending}
              />
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
                className="p-2 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Send className="w-5 h-5 text-white" />
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveSupportChat;
